import { useEffect, useRef, useState, useCallback } from "react";
import type { RadioTranscript } from "../types/radio";
import { radioTranscriptKey } from "../types/radio";

interface TranscriptionLogProps {
  lines: RadioTranscript[];
  onClear?: () => void;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--:--:--";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

/**
 * Scrolling log of transcript lines with timestamps.
 *
 * Auto-scrolls to bottom when new entries arrive (unless the user has scrolled up).
 * Shows a "Scroll to bottom" button and line count when auto-scroll is paused.
 * Uses `role="log"` for accessibility.
 */
export function TranscriptionLog({ lines, onClear }: TranscriptionLogProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
    setAutoScroll(true);
  }, []);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
    setAutoScroll(atBottom);
  }, []);

  useEffect(() => {
    if (autoScroll) {
      const el = containerRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }
  }, [lines.length, autoScroll]);

  return (
    <div className="transcription-log-container">
      <div className="transcription-log__header">
        <span className="transcription-log__title">Transcripts</span>
        <span className="transcription-log__count">
          {lines.length > 0 ? `${lines.length} line${lines.length !== 1 ? "s" : ""}` : ""}
        </span>
        {onClear && lines.length > 0 && (
          <button
            type="button"
            className="btn btn-sm transcription-log__clear-btn"
            onClick={onClear}
            aria-label="Clear transcripts"
            title="Clear transcripts"
          >
            Clear
          </button>
        )}
      </div>
      <div
        className="transcription-log"
        role="log"
        aria-label="Radio transcripts"
        ref={containerRef}
        onScroll={handleScroll}
      >
        {lines.length === 0 ? (
          <div className="transcription-log__empty">No transcripts yet</div>
        ) : (
          lines.map((line) => (
            <div
              className={`transcription-log__entry${line.partial ? " transcription-log__entry--partial" : ""}`}
              key={radioTranscriptKey(line)}
            >
              <span className="transcription-log__time">{formatTime(line.ts)}</span>
              {line.label && <span className="transcription-log__label">{line.label}</span>}
              <span className="transcription-log__text">{line.text}</span>
              {line.durationS !== null && (
                <span className="transcription-log__duration">{line.durationS.toFixed(1)}s</span>
              )}
            </div>
          ))
        )}
      </div>
      {!autoScroll && (
        <button
          type="button"
          className="transcription-log__scroll-btn"
          onClick={scrollToBottom}
          aria-label="Scroll to bottom"
        >
          ↓ Scroll to bottom
        </button>
      )}
    </div>
  );
}
